/**
 * Game-session endpoints used by the /play smart gate.
 *
 * The wrapper calls POST /game/token to mint a `gameToken` + matching
 * `sessionKey` and hand them to the game build. POST /game/token can reject
 * with these documented codes (see api/src/modules/game/game.controller.ts):
 *
 *   user_not_eligible            403 — status !== ACTIVE or enabled === false
 *   event_not_active             403 — phase is PRE_EVENT or POST_EVENT (not IN_EVENT)
 *   weekly_rules_consent_missing 403 — no current-week rulesConsent recorded
 *
 * The /play gate must pre-check phase + consent BEFORE calling this so the
 * user gets a useful UI ("come back later" / "agree to weekly rules") rather
 * than a generic 403.
 */
import { ApiError, apiFetch } from '../api';

export interface GameSession {
  sessionId: string;
  weekNumber: number;
  gameToken: string;
  gameRefreshToken: string;
  sessionKey: string;
  expiresAt: string;
  refreshExpiresAt: string;
}

export interface GameTokenError {
  code: string;
  message: string;
  status: number;
}

export type GameTokenResult =
  | { kind: 'ok'; session: GameSession }
  | { kind: 'error'; error: GameTokenError };

export async function issueGameToken(init?: RequestInit): Promise<GameTokenResult> {
  const res = await apiFetch('/game/token', { method: 'POST', ...init });
  const body = (await res.json().catch(() => ({}))) as
    | { status: number; data: GameSession }
    | { status: number; code: string; message: string };
  if (!res.ok) {
    if ('code' in body) {
      return { kind: 'error', error: { code: body.code, message: body.message, status: res.status } };
    }
    throw new ApiError(res.status, `game/token failed (${res.status})`, body);
  }
  return { kind: 'ok', session: (body as { data: GameSession }).data };
}
